import React, { useState, useRef, useCallback, useEffect } from 'react';
import VoiceBridge from '../services/voiceBridge';
import { isMobilePhone } from '../utils/deviceDetection';
import { TextToSpeech } from '../services/textToSpeech';
import { trackMessage } from '../services/analytics';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { processAssistantResponse } from '../utils/textProcessing';

const ChatWidget = ({ userEmail, selectedScenario }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [mode, setMode] = useState('text');
  const [isMobile, setIsMobile] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const chatContainerRef = useRef(null);
  const voiceBridgeRef = useRef(null);
  const ttsRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    setIsMobile(isMobilePhone());
  }, []);

  useEffect(() => {
    ttsRef.current = new TextToSpeech();
  }, []);

  useEffect(() => {
    const getUserSelectedCompany = async () => {
      if (!userEmail) return;
      
      try {
        const userRef = doc(db, 'userAccess', userEmail);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let userCompany = userData.currentScenario;
          
          // If no company is selected, set the first allowed scenario as default
          if (!userCompany) {
            const allowedScenarios = userData.scenarios;
            
            if (Array.isArray(allowedScenarios) && allowedScenarios.length > 0) {
              // Use the first allowed scenario as default
              userCompany = allowedScenarios[0];
            } else if (allowedScenarios === 'all') {
              // For admin users with 'all' access, fetch and use first available
              try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/companies`, {
                  headers: {
                    'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
                  }
                });
                
                if (response.ok) {
                  const companies = await response.json();
                  userCompany = companies.length > 0 ? companies[0] : null;
                }
              } catch (e) {
                console.error("Failed to fetch companies list:", e);
              }
            }
            
            // Save this default selection
            if (userCompany) {
              await updateDoc(userRef, {
                currentScenario: userCompany,
                lastUpdated: new Date()
              });
            }
          }
          
          // Tell the backend to use this company for this user
          if (userCompany) {
            await fetch(`${process.env.REACT_APP_AI_ENDPOINT_URL}/update-company`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'User-Email': userEmail
              },
              body: JSON.stringify({ 
                company_name: userCompany,
                user_id: userEmail 
              })
            });
          }
        }
      } catch (error) {
        console.error('Error getting user selected company:', error);
      }
    };
    
    getUserSelectedCompany();
  }, [userEmail]);

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  const updateAssistantResponse = useCallback((response) => {
    console.log('Received chunk:', response);
    const processedResponse = processAssistantResponse(response);
    setChatMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const lastMessage = newMessages[newMessages.length - 1];

        if (lastMessage && lastMessage.sender === 'Intellecsys AI') {
            lastMessage.message = processedResponse;  // Replace entire message
        } else {
            newMessages.push({
                sender: 'Intellecsys AI',
                message: processedResponse
            });
        }

        return newMessages;
    });
    scrollToBottom();
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => {
      const chatMessages = document.getElementById('chat-messages');
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    }, 0);
  };

  const handleTranscription = async (text) => {
    console.log('Received transcription:', text);
    
    // Stop any current TTS playback when user speaks
    if (ttsRef.current) {
        ttsRef.current.stopCurrent();
    }

    setChatMessages(prevMessages => [...prevMessages, {
        sender: 'You',
        message: text
    }]);
    scrollToBottom();
  };

  React.useEffect(() => {
    voiceBridgeRef.current = new VoiceBridge(userEmail);
    
    let currentResponse = '';
    
    // Only register one transcription handler that does both jobs
    voiceBridgeRef.current.on('transcription', (text) => {
        currentResponse = ''; // Reset for new conversation turn
        handleTranscription(text);
    });
    
    voiceBridgeRef.current.on('status', (message) => {
        if (message.includes('Connected to voice server')) {
            setIsListening(true);
        }
    });
    
    voiceBridgeRef.current.on('apiResponse', (chunk) => {
        console.log('Received chunk:', chunk);
        
        // Accumulate chunks here
        currentResponse += chunk;
        
        // Process the response to remove think tags and convert markdown
        const processedResponse = processAssistantResponse(currentResponse);
        
        setChatMessages(prevMessages => {
            const newMessages = [...prevMessages];
            const lastMessage = newMessages[newMessages.length - 1];

            if (lastMessage && lastMessage.sender === 'Intellecsys AI') {
                lastMessage.message = processedResponse;
            } else {
                newMessages.push({
                    sender: 'Intellecsys AI',
                    message: processedResponse
                });
            }

            return newMessages;
        });
        scrollToBottom();
    });

    return () => {
        if (voiceBridgeRef.current) {
            voiceBridgeRef.current.stop();
        }
    };
}, [userEmail]);

  const handleModeChange = (newMode) => {
    // If we're already in this mode, treat it as a toggle off
    if (mode === newMode) {
        setMode('text');
        if (voiceBridgeRef.current) {
            voiceBridgeRef.current.stop();
        }
        setIsListening(false);
        return;
    }
    
    setMode(newMode);
    if (voiceBridgeRef.current) {
        voiceBridgeRef.current.stop();
        setTimeout(() => {
            voiceBridgeRef.current.start(newMode);
            setIsListening(true);
        }, 100);
    }
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    autoResizeTextarea(e.target);
  };

  const autoResizeTextarea = (element) => {
    element.style.height = 'auto';
    element.style.height = (element.scrollHeight) + 'px';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !userEmail) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setChatMessages(prevMessages => [...prevMessages, { sender: 'You', message: userMessage }]);
    scrollToBottom();

    try {
      const API_URL = `${process.env.REACT_APP_AI_ENDPOINT_URL}/chat/stream`;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          user_id: userEmail
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantResponse = '';  // Accumulate full response

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log("Stream complete");
          break;
        }

        const chunk = decoder.decode(value);
        assistantResponse += chunk;  // Add chunk to full response
        updateAssistantResponse(assistantResponse);  // Update with full response
      }

      if (!assistantResponse) {
        console.log("No response content received");
        updateAssistantResponse("I'm having trouble responding. Can you try again?");
      }

    } catch (error) {
      console.error('Error:', error);
      updateAssistantResponse("Oops, I'm having trouble thinking right now. Can you try again?");
    }
  };

  const startListening = () => {
    if (voiceBridgeRef.current) {
        voiceBridgeRef.current.start(mode);
        setIsListening(true);
    }
  };

  return (
    <div id="chat-widget">
      <div className="flex items-center space-x-2">
        <button id="chat-toggle" onClick={toggleChat}>
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e8eaed">
              <path d="M280-240q-17 0-28.5-11.5T240-280v-80h520v-360h80q17 0 28.5 11.5T880-680v600L720-240H280ZM80-280v-560q0-17 11.5-28.5T120-880h520q17 0 28.5 11.5T680-840v360q0 17-11.5 28.5T640-440H240L80-280Zm520-240v-280H160v280h440Zm-440 0v-280 280Z"/>
            </svg>
          </span> Chat with our AI agent
        </button>
        {isChatOpen && (
          <div className="flex space-x-2">
            <button
              className={`px-4 py-2 rounded-lg transition duration-300 ${
                mode === 'text' 
                  ? 'bg-blue-400 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleModeChange('text')}
            >
              Text Mode
            </button>
            <button
              className={`px-4 py-2 rounded-lg transition duration-300 relative ${
                mode === 'mixed'
                  ? 'bg-blue-400 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleModeChange('mixed')}
            >
              Mixed Mode
              {mode === 'mixed' && isListening && (
                <span className="absolute right-1 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              )}
            </button>
            <button
              className={`px-4 py-2 rounded-lg transition duration-300 relative ${
                mode === 'full-voice'
                  ? 'bg-blue-400 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => handleModeChange('full-voice')}
            >
              Full Voice Mode
              {mode === 'full-voice' && isListening && (
                <span className="absolute right-1 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              )}
            </button>
          </div>
        )}
      </div>
      
      {isChatOpen && (
        <div id="chat-container" ref={chatContainerRef}>
          <div id="chat-messages">
            <div style={{ flexGrow: 1 }}></div>
            {chatMessages.map((msg, index) => (
              <div 
                key={index} 
                className={`chat-bubble ${
                  msg.sender === 'You' 
                    ? 'user-message' 
                    : msg.sender === 'System' 
                      ? 'system-message' 
                      : 'agent-message'
                }`}
              >
                <strong>{msg.sender === 'Intellecsys AI' ? 'AI Agent' : msg.sender}:</strong>{' '}
                {msg.sender === 'Intellecsys AI' ? (
                  <span dangerouslySetInnerHTML={{ __html: msg.message }} />
                ) : (
                  msg.message
                )}
              </div>
            ))}
          </div>
          <div id="chat-input-area">
            <textarea
              id="chat-input"
              value={inputMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={mode === 'mixed' ? "Mixed mode active..." : "Type a message..."}
              rows="1"
              disabled={mode === 'mixed'}
            />
            <button 
              id="chat-send" 
              onClick={sendMessage}
              disabled={mode === 'mixed'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FFFFFF">
                <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"/>
              </svg>
            </button>
          </div>
        </div>
      )}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

          #chat-widget {
            position: fixed;
            bottom: 20px;
            right: 20px;
            font-family: 'Roboto', sans-serif;
            z-index: 1000;
          }

          #chat-toggle {
            padding: 10px 20px;
            background: linear-gradient(145deg, #4CAF50, #36A166);
            color: white;
            border: none;
            cursor: pointer;
            border-radius: 25px;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 10px;
            transition: background 0.3s ease, box-shadow 0.3s ease;
          }

          #chat-toggle:hover {
            background: #36A166;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          }

          #chat-container {
            width: ${isMobile ? '100%' : '600px'};
            height: ${isMobile ? '80vh' : '515px'};
            min-width: ${isMobile ? '100%' : '375px'};
            min-height: ${isMobile ? '80vh' : '300px'};
            max-width: ${isMobile ? '100%' : '1000px'};
            max-height: ${isMobile ? '80vh' : '640px'};
            border: 1px solid #ccc;
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(10px);
            display: flex;
            flex-direction: column;
            margin-top: ${isMobile ? '0' : '10px'};
            border-radius: ${isMobile ? '0' : '20px'};
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            resize: ${isMobile ? 'none' : 'both'};
            position: relative;
          }

          #chat-messages {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
            display: flex;
            flex-direction: column;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          #chat-messages::-webkit-scrollbar {
            width: 0;
            height: 0;
            display: none;
          }

          #chat-messages > div {
            white-space: pre-wrap;
            margin-top: 10px;
          }

          #chat-input-area {
            display: flex;
            padding: 10px;
            border-top: 1px solid #ccc;
            background: #f9f9f9;
          }

          #chat-input {
            flex-grow: 1;
            padding: 10px;
            border: 1px solid #ccc;
            border-radius: 20px;
            font-size: 14px;
            resize: none;
            overflow-y: hidden;
            line-height: 1.5;
            max-height: 100px;
          }

          #chat-send {
            margin-left: 10px;
            padding: 10px;
            background-color: #36A166;
            color: white;
            border: none;
            cursor: pointer;
            border-radius: ${isMobile ? '20px' : '50%'};
            font-size: 16px;
            transition: background 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            width: ${isMobile ? 'auto' : '40px'};
            height: ${isMobile ? 'auto' : '40px'};
          }

          #chat-send:hover {
            background-color: #4CAF50;
          }

          .chat-bubble {
            max-width: 80%;
            padding: 12px 16px;
            border-radius: 20px;
            margin-bottom: 10px;
            word-wrap: break-word;
            font-size: 14px;
            line-height: 1.5;
          }

          .user-message {
            align-self: flex-end;
            background: linear-gradient(145deg, #DCF8C6, #B5EAD8);
            color: #333;
          }

          .agent-message {
            align-self: flex-start;
            background-color: #E0E0E0;
            color: #333;
          }

          .system-message {
            align-self: center;
            background-color: #ffebee;
            color: #c62828;
            padding: 8px 16px;
            border-radius: 20px;
            margin: 10px 0;
            font-size: 14px;
          }

          /* Styles for rendered HTML content in AI messages */
          .agent-message h1 {
            font-size: 1.5em;
            font-weight: bold;
            margin: 10px 0 8px 0;
            color: #333;
          }

          .agent-message h2 {
            font-size: 1.3em;
            font-weight: bold;
            margin: 8px 0 6px 0;
            color: #333;
          }

          .agent-message h3 {
            font-size: 1.1em;
            font-weight: bold;
            margin: 6px 0 4px 0;
            color: #333;
          }

          .agent-message strong {
            font-weight: bold;
          }

          .agent-message em {
            font-style: italic;
          }

          ${isMobile ? `
            #chat-widget {
              bottom: 0;
              right: 0;
              left: 0;
            }

            #chat-toggle {
              width: 100%;
              border-radius: 0;
              justify-content: center;
            }
          ` : ''}
        `}
      </style>
    </div>
  );
};

export default ChatWidget; 